import type { CanvasTarget } from '../types';
import runtimeCopy from '@/i18n/ns/workspace-knowledge-runtime';
import type {
  PageAuthorKind,
  PageProposal,
  PageProposalKind,
  PageSuggestion,
  RevisionPeople,
} from './types';

function validDate(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}
type RuntimeCopy = Readonly<Record<keyof typeof runtimeCopy.en, string>>;
const EN = runtimeCopy.en as RuntimeCopy;

/** "15 Sept", with the year only when it is not this year. */
export function shortDate(
  value: string | null | undefined,
  localeOrNow: string | number = 'en-GB',
  now = Date.now(),
) {
  const locale =
    typeof localeOrNow === 'string' ? (localeOrNow === 'en' ? 'en-GB' : localeOrNow) : 'en-GB';
  if (typeof localeOrNow === 'number') now = localeOrNow;
  const date = validDate(value);
  if (!date) return null;
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/** "3 hours ago" for recent work, the date after a week. */
export function relativeAge(
  value: string | null | undefined,
  localeOrNow: string | number = 'en-GB',
  now = Date.now(),
  copy: RuntimeCopy = EN,
) {
  const locale = typeof localeOrNow === 'string' ? localeOrNow : 'en-GB';
  if (typeof localeOrNow === 'number') now = localeOrNow;
  const date = validDate(value);
  if (!date) return null;
  const seconds = Math.round((now - date.getTime()) / 1000);
  if (seconds < 60) return copy.justNow;
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (seconds < 3600) return format.format(-Math.floor(seconds / 60), 'minute');
  if (seconds < 86_400) return format.format(-Math.floor(seconds / 3600), 'hour');
  if (seconds < 7 * 86_400) return format.format(-Math.floor(seconds / 86_400), 'day');
  return shortDate(value, locale, now);
}

export const plural = (value: number, one: string, many = `${one}s`, locale = 'en-GB') => {
  const forms = one.match(/^one:(.*?)\|other:(.*)$/u);
  const singular = forms?.[1] ?? one;
  const pluralForm = forms?.[2] ?? many;
  return `${value.toLocaleString(locale)} ${value === 1 ? singular : pluralForm}`;
};

/**
 * How a revision became the page's text, for the meta line and history:
 * "edited by Sara Holm", "accepted Poneva's suggestion", "imported".
 */
export function revisionOrigin(people: RevisionPeople, copy: RuntimeCopy = EN) {
  if (people.editedByName) return copy.editedBy.replace('{name}', people.editedByName);
  if (people.authorKind === 'import') return copy.imported;
  if (people.approvedByName) return copy.acceptedBy.replace('{name}', people.approvedByName);
  if (people.authorKind === 'user' && people.authorName)
    return copy.editedBy.replace('{name}', people.authorName);
  return null;
}

/** Whose suggestion a revision accepted: "Poneva's suggestion", "Erik Lind's suggestion". */
export function acceptedSuggestionLabel(people: RevisionPeople, copy: RuntimeCopy = EN) {
  if (!people.proposalId || people.editedByName) return null;
  if (people.authorKind === 'assistant') return copy.acceptedPoneva;
  if (people.authorKind === 'external_agent')
    return copy.acceptedFrom.replace('{name}', people.clientName ?? copy.connectedAgent);
  if (people.authorKind === 'user' && people.authorName)
    return copy.acceptedNamed.replace('{name}', people.authorName);
  return copy.acceptedSuggestion;
}

/**
 * Who made a suggestion and how, as the card names them: a person, "Poneva"
 * in someone's chat or from a meeting, or a connected agent by its display
 * name. `detail` is the second line ("In Anna's chat", "Edited the page").
 */
export function suggestionAuthor(
  suggestion: Pick<
    PageSuggestion,
    'authorKind' | 'authorName' | 'clientName' | 'isMine' | 'kind' | 'source'
  >,
  copy: RuntimeCopy = EN,
): { name: string; detail: string } {
  const person = suggestion.isMine ? copy.youLower : (suggestion.authorName ?? copy.colleagueLower);
  const kind: PageAuthorKind | null = suggestion.authorKind;
  if (kind === 'assistant') {
    if (suggestion.source?.kind === 'meeting')
      return {
        name: 'Poneva',
        detail: suggestion.source.title
          ? `${copy.fromMeeting} “${suggestion.source.title}”`
          : copy.fromMeeting,
      };
    return {
      name: 'Poneva',
      detail: suggestion.isMine
        ? copy.inYourChat
        : suggestion.authorName
          ? copy.inNamedChat.replace('{name}', suggestion.authorName)
          : copy.inAChat,
    };
  }
  if (kind === 'external_agent')
    return {
      name: suggestion.clientName ?? copy.connectedAgent,
      detail: copy.connectedAgentFor.replace('{name}', person),
    };
  if (kind === 'import') return { name: copy.repositoryImport, detail: copy.importedText };
  return {
    name: suggestion.isMine ? copy.you : (suggestion.authorName ?? copy.colleague),
    detail: kindAction(suggestion.kind, copy),
  };
}

const kindAction = (kind: PageProposalKind, copy: RuntimeCopy) =>
  ({
    update_page: copy.editedPage,
    create_page: copy.suggestedNewPage,
    move_page: copy.suggestedMove,
    archive_page: copy.suggestedArchive,
  })[kind];

/** The review list's "who" for a pending proposal, in the same words as a card. */
export function proposalAuthor(proposal: PageProposal, copy: RuntimeCopy = EN) {
  return suggestionAuthor(
    {
      authorKind: proposal.authorKind,
      authorName: proposal.createdByName,
      clientName: proposal.clientName,
      isMine: proposal.isMine,
      kind: proposal.kind,
      source: null,
    },
    copy,
  );
}

export function characterLabel(value: number | null, locale = 'en-GB', copy: RuntimeCopy = EN) {
  if (value === null) return null;
  return plural(value, copy.character, undefined, locale);
}

export const sectionAnchor = (id: string) => `page-section-${id}`;

export function prefersReducedMotion() {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

/** A page canvas target, with an optional section, revision or suggestion in focus. */
export function pageTarget(focus: {
  id: string;
  section?: string;
  revision?: string;
  suggestion?: string;
  tab?: string;
  full?: boolean;
}): CanvasTarget {
  return {
    kind: 'page',
    id: focus.id,
    ...(focus.section ? { section: focus.section } : {}),
    ...(focus.revision ? { revision: focus.revision } : {}),
    ...(focus.suggestion ? { suggestion: focus.suggestion } : {}),
    ...(focus.tab ? { tab: focus.tab } : {}),
    ...(focus.full ? { full: true } : {}),
  };
}
